/**
 * shopify-theme/assets/returns-flow.js
 *
 * Self-serve returns/warranty wizard. Vanilla JS, no build step — matches
 * the style of the other thegrint.shop theme integrations in this account.
 *
 * IMPORTANT: this file only handles UX flow and calls the backend for the
 * actual eligibility decision (see netlify/functions/submit-return.js).
 * The 45-day / rangefinder-warranty / 1-year policy logic is NOT
 * duplicated here — the server is the single source of truth.
 *
 * TODO (customize before launch): the TROUBLESHOOTING_STEPS content below
 * is placeholder copy — replace with your real troubleshooting guidance
 * per reason/product.
 */

(function () {
  const root = document.getElementById('returns-flow-app');
  if (!root) return;

  const API_BASE = root.dataset.apiBase || '/returns/api';
  const SUPPORT_EMAIL = root.dataset.supportEmail || 'shop@thegrint.com';
  const POLICY_URL = root.dataset.policyUrl || '/policies/refund-policy';

  const INTERNATIONAL_DISCLAIMER_HTML = `Are you attempting a Return or Exchange from outside the US? Please contact <a href="mailto:${escapeHtml(SUPPORT_EMAIL)}">${escapeHtml(SUPPORT_EMAIL)}</a> and review our <a href="${escapeHtml(POLICY_URL)}">Return and Refund Policy</a> as additional charges may apply.`;

  const REASONS = [
    { value: 'changed_mind', label: 'I changed my mind' },
    { value: 'wrong_size', label: "It doesn't fit / wrong size" },
    { value: 'not_as_described', label: 'Not as described' },
    { value: 'arrived_damaged', label: 'It arrived damaged' },
    { value: 'warranty_issue', label: "It's not working correctly (possible defect)" },
    { value: 'other_issue', label: 'There was another issue' },
  ];

  // Placeholder troubleshooting content — customize per reason.
  const TROUBLESHOOTING_STEPS = {
    wrong_size: [
      'Have you checked our sizing guide against your measurements?',
      'Would an exchange for a different size solve this, instead of a return?',
    ],
  };

  // Rangefinder-specific troubleshooting for "It's not working correctly
  // (possible defect)" — shown instead of a generic troubleshooting list
  // when the item being returned is tagged `rangefinder`.
  const RANGEFINDER_ISSUE_TYPES = [
    {
      value: 'cannot_connect',
      label: 'I cannot connect it to my app',
      tips: [
        'Check that the Bluetooth symbol is visible inside the lens; press the Mode or Bluetooth button once to turn on Bluetooth.',
        'Check the connection in the app\u2019s Pairing menu for 3 green checkmarks, and correct any issues if needed.',
        'Close the TheGrint app, reopen it, and try again.',
        'Restart your phone and try again.',
      ],
    },
    {
      value: 'no_distances',
      label: 'Front/Back/Center distances never show up',
      tips: [
        'Check that the Bluetooth symbol is visible inside the lens, or press the Mode/Bluetooth button.',
        'Check the Pairing menu for 3 green checkmarks.',
        'During a live round, check that GPS is turned ON.',
        'Unpair the rangefinder from your account, then re-pair it.',
      ],
    },
    {
      value: 'yardage_diff',
      label: 'I am getting 10-20 yard difference on every shot',
      tips: [
        'Check if the unit is set to Yards or Meters correctly; if incorrect, press and hold the Mode (M) or M/Y button for 2-3 seconds to change it.',
      ],
    },
    {
      value: 'malfunction',
      label: 'There is a malfunction',
      subOptions: [
        { value: 'screen_issue', label: 'Screen issue (e.g., pixels missing, screen does not turn on)' },
        { value: 'battery_issue', label: 'Battery issue (e.g., will not charge)' },
        { value: 'laser_distance_issue', label: 'Laser Distance issue (e.g., erratic distances, always reads a single value)' },
        { value: 'other', label: 'Other' },
      ],
    },
  ];

  // Mirrors the standard-window boundary from netlify/functions/lib/eligibility.js
  // (day_zero = order createdAt + 7 days; standard window = 45 days after
  // that). UI-ONLY — used to decide which wizard screens to show (e.g.
  // whether to offer an immediate exchange for a defect item). The server
  // is still the sole authority on actual eligibility at submit time; keep
  // these constants in sync with eligibility.js if the policy ever changes.
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const DAY_ZERO_OFFSET_DAYS = 7;
  const STANDARD_WINDOW_DAYS = 45;

  function isWithinStandardWindow() {
    if (!state.order || !state.order.createdAt) return true; // unknown — fail open to the normal flow
    const dayZero = new Date(state.order.createdAt).getTime() + DAY_ZERO_OFFSET_DAYS * MS_PER_DAY;
    const standardDeadline = dayZero + STANDARD_WINDOW_DAYS * MS_PER_DAY;
    return Date.now() <= standardDeadline;
  }

  const state = {
    wizardToken: null,
    order: null,
    lineItems: [],
    queue: [], // items left to process
    current: null, // { lineItemId, fulfillmentLineItemId, quantity, productId, productTags, title, reason, exchangeVariantId?, exchangeVariantTitle?, ... }
    finalSelections: [], // items proceeding to submission
    resolvedItems: [], // items the customer resolved without returning
  };

  render(startStep());

  // ── Step renderers ─────────────────────────────────────────────────────

  function startStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>What would you like to do?</h2>
      <div class="rf-start-choices">
        <button class="rf-btn-primary" id="rf-start-return">Return/Exchange an Item</button>
        <button class="rf-btn-secondary" id="rf-start-report">Report an Issue</button>
      </div>
      <p class="rf-sub rf-disclaimer">${INTERNATIONAL_DISCLAIMER_HTML}</p>
    `;
    el.querySelector('#rf-start-return').addEventListener('click', () => render(lookupStep()));
    el.querySelector('#rf-start-report').addEventListener('click', () => render(reportIssueStep()));
    return el;
  }

  function reportIssueStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Report an Issue</h2>
      <div class="rf-start-choices">
        <button class="rf-btn-primary" id="rf-report-not-received">My Package shows delivered but it was not received</button>
        <button class="rf-btn-primary" id="rf-report-wrong-item">I received the wrong item</button>
      </div>
      <button class="rf-btn-secondary" id="rf-report-back">Back</button>
    `;
    el.querySelector('#rf-report-not-received').addEventListener('click', () => render(packageNotReceivedStep()));
    el.querySelector('#rf-report-wrong-item').addEventListener('click', () => render(wrongItemReceivedStep()));
    el.querySelector('#rf-report-back').addEventListener('click', () => render(startStep()));
    return el;
  }

  function packageNotReceivedStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    const tips = [
      'Sometimes packages are marked as \u201cdelivered\u201d by the carrier a day or two before they physically arrive. Check your mail today and tomorrow to see if they have arrived.',
      'The package might have been left in an alternative location around your delivery address (e.g., back door, side door, or with a building manager).',
      'The Package might have been delivered to a neighbor by mistake. Safely check with neighbors or a building manager (if applicable) to see if it was delivered to them by mistake.',
      'If none of these tips help you find the package, contact your local carrier (UPS.com or USPS.com) and submit a lost item claim. Send the claim number (required) to our team at shop@thegrint.com and we can work with you on replacing the item.',
    ];
    el.innerHTML = `
      <h2>Package Shows Delivered but Not Received</h2>
      <ul class="rf-troubleshoot-list">
        ${tips.map((t) => `<li>${escapeHtml(t)}</li>`).join('')}
      </ul>
      <button class="rf-btn-secondary" id="rf-report-back">Back</button>
    `;
    el.querySelector('#rf-report-back').addEventListener('click', () => render(reportIssueStep()));
    return el;
  }

  function wrongItemReceivedStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>I Received the Wrong Item</h2>
      <p>Email us at <a href="mailto:${escapeHtml(SUPPORT_EMAIL)}">${escapeHtml(SUPPORT_EMAIL)}</a> with a description and photo of the item you received. Please include your 4-5 digit Order number in the subject line of the email.</p>
      <button class="rf-btn-secondary" id="rf-report-back">Back</button>
    `;
    el.querySelector('#rf-report-back').addEventListener('click', () => render(reportIssueStep()));
    return el;
  }

  function hasRangefinderTag(tags) {
    return (tags || []).map((t) => String(t).toLowerCase().trim()).includes('rangefinder');
  }

  function lookupStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Start a Return</h2>
      <p class="rf-sub">Enter your order number and the email used at checkout.</p>
      <label>Order Number</label>
      <input type="text" id="rf-order-number" placeholder="#1001" />
      <label>Email</label>
      <input type="email" id="rf-email" placeholder="you@example.com" />
      <div class="rf-error" id="rf-lookup-error"></div>
      <button class="rf-btn-primary" id="rf-lookup-submit">Find My Order</button>
      <button class="rf-btn-secondary" id="rf-lookup-back">Back</button>
    `;
    el.querySelector('#rf-lookup-back').addEventListener('click', () => render(startStep()));
    el.querySelector('#rf-lookup-submit').addEventListener('click', async () => {
      const orderNumber = el.querySelector('#rf-order-number').value.trim();
      const email = el.querySelector('#rf-email').value.trim();
      const errorEl = el.querySelector('#rf-lookup-error');
      errorEl.textContent = '';
      if (!orderNumber || !email) {
        errorEl.textContent = 'Please enter both your order number and email.';
        return;
      }
      setLoading(el, '#rf-lookup-submit', true);
      try {
        const res = await postJson('/order-lookup', { orderNumber, email });
        if (!res.success) {
          if (res.error === 'international_order') {
            render(internationalOrderStep());
            return;
          }
          errorEl.textContent = res.message || 'Could not verify your order.';
          setLoading(el, '#rf-lookup-submit', false);
          return;
        }
        state.wizardToken = res.wizardToken;
        state.order = res.order;
        state.lineItems = res.lineItems;
        state.queue = [];
        render(itemSelectStep());
      } catch (err) {
        errorEl.textContent = 'Something went wrong. Please try again.';
        setLoading(el, '#rf-lookup-submit', false);
      }
    });
    return el;
  }

  function internationalOrderStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>This Order Ships Outside the US</h2>
      <p class="rf-sub">${INTERNATIONAL_DISCLAIMER_HTML}</p>
      <button class="rf-btn-secondary" id="rf-intl-back">Back</button>
    `;
    el.querySelector('#rf-intl-back').addEventListener('click', () => render(startStep()));
    return el;
  }

  function itemSelectStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';

    if (state.lineItems.length === 0) {
      el.innerHTML = `
        <h2>No Items Available to Return</h2>
        <p class="rf-sub">Order ${escapeHtml(state.order.name)}</p>
        <p>This order hasn't shipped yet, so there's nothing eligible for a return
        just yet. Once it ships, come back here with your order number and email
        to start a return.</p>
        <p>Need to cancel or change this order before it ships? Contact us at
        <a href="mailto:${escapeHtml(SUPPORT_EMAIL)}">${escapeHtml(SUPPORT_EMAIL)}</a>.</p>
      `;
      return el;
    }

    el.innerHTML = `
      <h2>Select Item(s) to Return</h2>
      <p class="rf-sub">Order ${escapeHtml(state.order.name)}</p>
      <div class="rf-items"></div>
      <div class="rf-error" id="rf-items-error"></div>
      <button class="rf-btn-primary" id="rf-items-continue">Continue</button>
      <button class="rf-btn-secondary" id="rf-items-back">Back</button>
    `;
    el.querySelector('#rf-items-back').addEventListener('click', () => render(lookupStep()));
    const itemsWrap = el.querySelector('.rf-items');
    state.lineItems.forEach((item, i) => {
      const row = document.createElement('label');
      row.className = 'rf-item-row';
      row.innerHTML = `
        <input type="checkbox" data-idx="${i}" />
        <span>${escapeHtml(item.title)} (qty ${item.refundableQuantity})</span>
      `;
      itemsWrap.appendChild(row);
    });
    el.querySelector('#rf-items-continue').addEventListener('click', () => {
      const checked = [...el.querySelectorAll('input[type="checkbox"]:checked')].map((cb) => state.lineItems[Number(cb.dataset.idx)]);
      if (checked.length === 0) {
        el.querySelector('#rf-items-error').textContent = 'Please select at least one item.';
        return;
      }
      state.queue = checked.map((item) => ({ ...item, quantity: 1 }));
      processNextInQueue();
    });
    return el;
  }

  function processNextInQueue() {
    if (state.queue.length === 0) {
      render(reviewStep());
      return;
    }
    state.current = state.queue.shift();
    render(reasonStep());
  }

  function reasonStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Why are you returning this item?</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <div class="rf-reasons"></div>
      <button class="rf-btn-primary" id="rf-reason-continue" disabled>Continue</button>
      <button class="rf-btn-secondary" id="rf-reason-back">Back</button>
    `;
    el.querySelector('#rf-reason-back').addEventListener('click', () => {
      state.queue.unshift(state.current);
      render(itemSelectStep());
    });
    const wrap = el.querySelector('.rf-reasons');
    REASONS.forEach((r) => {
      const row = document.createElement('label');
      row.className = 'rf-radio-row';
      row.innerHTML = `<input type="radio" name="rf-reason" value="${r.value}" /> ${escapeHtml(r.label)}`;
      wrap.appendChild(row);
    });
    el.querySelectorAll('input[name="rf-reason"]').forEach((input) => {
      input.addEventListener('change', () => {
        el.querySelector('#rf-reason-continue').disabled = false;
      });
    });
    el.querySelector('#rf-reason-continue').addEventListener('click', () => {
      const selected = el.querySelector('input[name="rf-reason"]:checked').value;
      state.current.reason = selected;

      if (selected === 'warranty_issue') {
        if (hasRangefinderTag(state.current.productTags)) {
          render(rangefinderIssueTypeStep());
        } else {
          render(defectDescriptionStep());
        }
        return;
      }

      if (selected === 'other_issue') {
        render(otherIssueDescriptionStep());
        return;
      }

      const troubleshooting = TROUBLESHOOTING_STEPS[selected];
      if (troubleshooting && troubleshooting.length > 0) {
        render(troubleshootStep(troubleshooting));
      } else {
        proceedFromReason();
      }
    });
    return el;
  }

  function troubleshootStep(steps, onStillIssue, onBack) {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Before You Return It — Quick Troubleshooting</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <ul class="rf-troubleshoot-list">
        ${steps.map((s) => `<li>${escapeHtml(s)}</li>`).join('')}
      </ul>
      <div class="rf-troubleshoot-actions">
        <button class="rf-btn-secondary" id="rf-resolved">This fixed it — I don't need to return it</button>
        <button class="rf-btn-primary" id="rf-still-return">Still having the issue — continue return</button>
      </div>
      <button class="rf-btn-secondary" id="rf-troubleshoot-back">Back</button>
    `;
    el.querySelector('#rf-resolved').addEventListener('click', () => {
      state.resolvedItems.push(state.current);
      processNextInQueue();
    });
    el.querySelector('#rf-still-return').addEventListener('click', () => (onStillIssue || proceedFromReason)());
    el.querySelector('#rf-troubleshoot-back').addEventListener('click', () => (onBack || (() => render(reasonStep())))());
    return el;
  }

  // Rangefinder + "It's not working correctly" — pick an issue category.
  function rangefinderIssueTypeStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>What's going on with your rangefinder?</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <div class="rf-reasons"></div>
      <button class="rf-btn-primary" id="rf-issue-type-continue" disabled>Continue</button>
      <button class="rf-btn-secondary" id="rf-issue-type-back">Back</button>
    `;
    el.querySelector('#rf-issue-type-back').addEventListener('click', () => render(reasonStep()));
    const wrap = el.querySelector('.rf-reasons');
    RANGEFINDER_ISSUE_TYPES.forEach((t) => {
      const row = document.createElement('label');
      row.className = 'rf-radio-row';
      row.innerHTML = `<input type="radio" name="rf-issue-type" value="${t.value}" /> ${escapeHtml(t.label)}`;
      wrap.appendChild(row);
    });
    el.querySelectorAll('input[name="rf-issue-type"]').forEach((input) => {
      input.addEventListener('change', () => {
        el.querySelector('#rf-issue-type-continue').disabled = false;
      });
    });
    el.querySelector('#rf-issue-type-continue').addEventListener('click', () => {
      const selected = el.querySelector('input[name="rf-issue-type"]:checked').value;
      const issueType = RANGEFINDER_ISSUE_TYPES.find((t) => t.value === selected);
      state.current.issueType = issueType.value;

      if (issueType.subOptions) {
        render(malfunctionTypeStep(issueType));
        return;
      }

      render(troubleshootStep(issueType.tips, () => {
        state.current.issueDetail = issueType.label;
        render(warrantyDetailsStep());
      }, () => render(rangefinderIssueTypeStep())));
    });
    return el;
  }

  // "There is a malfunction" — pick the specific malfunction sub-type. No
  // troubleshooting tips here, just categorization for staff.
  function malfunctionTypeStep(issueType) {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>What kind of malfunction?</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <div class="rf-reasons"></div>
      <button class="rf-btn-primary" id="rf-malfunction-continue" disabled>Continue</button>
      <button class="rf-btn-secondary" id="rf-malfunction-back">Back</button>
    `;
    const wrap = el.querySelector('.rf-reasons');
    issueType.subOptions.forEach((s) => {
      const row = document.createElement('label');
      row.className = 'rf-radio-row';
      row.innerHTML = `<input type="radio" name="rf-malfunction-type" value="${s.value}" /> ${escapeHtml(s.label)}`;
      wrap.appendChild(row);
    });
    el.querySelectorAll('input[name="rf-malfunction-type"]').forEach((input) => {
      input.addEventListener('change', () => {
        el.querySelector('#rf-malfunction-continue').disabled = false;
      });
    });
    el.querySelector('#rf-malfunction-back').addEventListener('click', () => render(rangefinderIssueTypeStep()));
    el.querySelector('#rf-malfunction-continue').addEventListener('click', () => {
      const selected = el.querySelector('input[name="rf-malfunction-type"]:checked').value;
      const subOption = issueType.subOptions.find((s) => s.value === selected);
      state.current.issueDetail = `${issueType.label} \u2014 ${subOption.label}`;
      render(warrantyDetailsStep());
    });
    return el;
  }

  // Non-rangefinder "It's not working correctly" — just a description, no
  // photos, no troubleshooting tips. Stored as the Return Reason description
  // if the return is approved.
  function defectDescriptionStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Tell Us What's Wrong</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <label>Describe the issue</label>
      <textarea id="rf-defect-description" rows="4" placeholder="What's not working correctly?"></textarea>
      <div class="rf-error" id="rf-defect-error"></div>
      <button class="rf-btn-primary" id="rf-defect-continue">Continue</button>
      <button class="rf-btn-secondary" id="rf-defect-back">Back</button>
    `;
    el.querySelector('#rf-defect-back').addEventListener('click', () => render(reasonStep()));
    el.querySelector('#rf-defect-continue').addEventListener('click', () => {
      const description = el.querySelector('#rf-defect-description').value.trim();
      if (!description) {
        el.querySelector('#rf-defect-error').textContent = 'Please describe the issue.';
        return;
      }
      state.current.description = description;
      if (isWithinStandardWindow()) {
        // Still inside the standard window — this auto-approves as a
        // regular return regardless of reason, so offer the same
        // refund/exchange choice as any other reason.
        proceedFromReason();
      } else {
        state.finalSelections.push(state.current);
        processNextInQueue();
      }
    });
    return el;
  }

  // "There was another issue" — unlike defectDescriptionStep (warranty_issue,
  // which skips straight to the next item), this is a normal return reason
  // so it continues on to the refund/exchange choice like any other reason.
  function otherIssueDescriptionStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Tell Us More</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <label>Describe the issue</label>
      <textarea id="rf-other-issue-description" rows="4" placeholder="What happened?"></textarea>
      <div class="rf-error" id="rf-other-issue-error"></div>
      <button class="rf-btn-primary" id="rf-other-issue-continue">Continue</button>
      <button class="rf-btn-secondary" id="rf-other-issue-back">Back</button>
    `;
    el.querySelector('#rf-other-issue-back').addEventListener('click', () => render(reasonStep()));
    el.querySelector('#rf-other-issue-continue').addEventListener('click', () => {
      const description = el.querySelector('#rf-other-issue-description').value.trim();
      if (!description) {
        el.querySelector('#rf-other-issue-error').textContent = 'Please describe the issue.';
        return;
      }
      state.current.description = description;
      proceedFromReason();
    });
    return el;
  }

  function proceedFromReason() {
    render(exchangeChoiceStep());
  }

  function exchangeChoiceStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Refund or Exchange?</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <div class="rf-radio-row-group">
        <label class="rf-radio-row"><input type="radio" name="rf-exchange-choice" value="refund" checked /> Return it for a refund</label>
        <label class="rf-radio-row"><input type="radio" name="rf-exchange-choice" value="exchange" /> Start an Exchange</label>
      </div>
      <div class="rf-error" id="rf-exchange-choice-error"></div>
      <button class="rf-btn-primary" id="rf-exchange-choice-continue">Continue</button>
      <button class="rf-btn-secondary" id="rf-exchange-choice-back">Back</button>
    `;
    el.querySelector('#rf-exchange-choice-back').addEventListener('click', () => render(reasonStep()));
    el.querySelector('#rf-exchange-choice-continue').addEventListener('click', async () => {
      const choice = el.querySelector('input[name="rf-exchange-choice"]:checked').value;
      if (choice === 'refund') {
        state.finalSelections.push(state.current);
        processNextInQueue();
        return;
      }
      if (!state.current.productId) {
        el.querySelector('#rf-exchange-choice-error').textContent = 'Sorry, exchanges aren\u2019t available for this item. Please choose a refund instead.';
        return;
      }
      setLoading(el, '#rf-exchange-choice-continue', true);
      try {
        const res = await postJson('/exchange-variants', {
          wizardToken: state.wizardToken,
          productId: state.current.productId,
        });
        if (!res.success || !res.variants || res.variants.length === 0) {
          el.querySelector('#rf-exchange-choice-error').textContent = 'No exchange options are currently available for this item. Please choose a refund instead.';
          setLoading(el, '#rf-exchange-choice-continue', false);
          return;
        }
        render(exchangeVariantStep(res.variants));
      } catch (err) {
        el.querySelector('#rf-exchange-choice-error').textContent = 'Something went wrong loading exchange options. Please try again, or choose a refund.';
        setLoading(el, '#rf-exchange-choice-continue', false);
      }
    });
    return el;
  }

  function exchangeVariantStep(variants) {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Choose Your Replacement</h2>
      <p class="rf-sub">${escapeHtml(state.current.title)}</p>
      <div class="rf-variants"></div>
      <div class="rf-error" id="rf-variant-error"></div>
      <button class="rf-btn-primary" id="rf-variant-continue" disabled>Confirm Exchange</button>
      <button class="rf-btn-secondary" id="rf-variant-back">Back</button>
    `;
    const wrap = el.querySelector('.rf-variants');
    variants.forEach((v) => {
      const row = document.createElement('label');
      row.className = 'rf-radio-row';
      row.innerHTML = `<input type="radio" name="rf-variant" value="${escapeHtml(v.id)}" /> ${escapeHtml(v.title)}${v.price ? ` — $${escapeHtml(v.price)}` : ''}`;
      wrap.appendChild(row);
    });
    el.querySelectorAll('input[name="rf-variant"]').forEach((input) => {
      input.addEventListener('change', () => {
        el.querySelector('#rf-variant-continue').disabled = false;
      });
    });
    el.querySelector('#rf-variant-back').addEventListener('click', () => render(exchangeChoiceStep()));
    el.querySelector('#rf-variant-continue').addEventListener('click', () => {
      const selectedId = el.querySelector('input[name="rf-variant"]:checked').value;
      const selectedVariant = variants.find((v) => v.id === selectedId);
      state.current.exchangeVariantId = selectedVariant.id;
      state.current.exchangeVariantTitle = selectedVariant.title;
      state.finalSelections.push(state.current);
      processNextInQueue();
    });
    return el;
  }

  function warrantyDetailsStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Tell Us About the Issue</h2>
      <p class="rf-sub">${state.current.issueDetail ? escapeHtml(state.current.issueDetail) : 'This helps our team review your warranty claim.'}</p>
      <label>Describe the defect</label>
      <textarea id="rf-description" rows="4" placeholder="What's happening with the unit?"></textarea>
      <label>Photos (optional, but helps speed up review)</label>
      <input type="file" id="rf-photos" accept="image/*" multiple />
      <div class="rf-error" id="rf-warranty-error"></div>
      <button class="rf-btn-primary" id="rf-warranty-continue">Submit for Review</button>
      <button class="rf-btn-secondary" id="rf-warranty-back">Back</button>
    `;
    el.querySelector('#rf-warranty-back').addEventListener('click', () => render(reasonStep()));
    el.querySelector('#rf-warranty-continue').addEventListener('click', async () => {
      const description = el.querySelector('#rf-description').value.trim();
      const files = [...el.querySelector('#rf-photos').files];
      const errorEl = el.querySelector('#rf-warranty-error');
      if (!description) {
        errorEl.textContent = 'Please describe the issue.';
        return;
      }
      setLoading(el, '#rf-warranty-continue', true);
      try {
        const photoUrls = [];
        for (const file of files) {
          const url = await uploadPhoto(file);
          if (url) photoUrls.push(url);
        }
        state.current.description = state.current.issueDetail ? `${state.current.issueDetail}: ${description}` : description;
        state.current.photoUrls = photoUrls;
        // Offer the same refund/exchange choice as any other reason,
        // whether this ends up standard-approved (within the 45-day
        // window) or routed to staff warranty review (past it, within the
        // 1-year rangefinder warranty) — either way the customer's
        // exchange preference (if any) travels with the claim/return so
        // staff can act on it directly instead of following up separately.
        proceedFromReason();
      } catch (err) {
        errorEl.textContent = 'Could not upload photos. You can continue without them, or try again.';
        setLoading(el, '#rf-warranty-continue', false);
      }
    });
    return el;
  }

  async function uploadPhoto(file) {
    const prep = await postJson('/photo-upload-url', {
      wizardToken: state.wizardToken,
      filename: file.name,
      mimeType: file.type,
      fileSize: file.size,
    });
    if (!prep.success) return null;

    const formData = new FormData();
    (prep.parameters || []).forEach((p) => formData.append(p.name, p.value));
    formData.append('file', file);

    await fetch(prep.uploadUrl, { method: 'POST', body: formData });
    return prep.resourceUrl;
  }

  function reviewStep() {
    const el = document.createElement('div');
    el.className = 'rf-step';

    if (state.finalSelections.length === 0) {
      el.innerHTML = `
        <h2>All Set!</h2>
        <p class="rf-sub">Glad the troubleshooting steps resolved things — no return needed.</p>
        <button class="rf-btn-secondary" id="rf-start-new">Start a New Return</button>
      `;
      el.querySelector('#rf-start-new').addEventListener('click', () => resetWizard());
      return el;
    }

    el.innerHTML = `
      <h2>Review Your Return</h2>
      <ul class="rf-review-list">
        ${state.finalSelections.map((item) => `<li>${escapeHtml(item.title)} — ${escapeHtml(REASONS.find((r) => r.value === item.reason)?.label || item.reason)}${item.exchangeVariantId ? ` <br/><span class="rf-sub">Exchange for: ${escapeHtml(item.exchangeVariantTitle)}</span>` : ''}</li>`).join('')}
      </ul>
      <button class="rf-btn-primary" id="rf-submit">Submit Return</button>
      <div class="rf-error" id="rf-submit-error"></div>
    `;
    el.querySelector('#rf-submit').addEventListener('click', async () => {
      setLoading(el, '#rf-submit', true);
      try {
        const res = await postJson('/submit-return', {
          wizardToken: state.wizardToken,
          selections: state.finalSelections,
        });
        if (!res.success) {
          el.querySelector('#rf-submit-error').textContent = res.message || 'Something went wrong.';
          setLoading(el, '#rf-submit', false);
          return;
        }
        render(outcomeStep(res.results));
      } catch (err) {
        el.querySelector('#rf-submit-error').textContent = 'Something went wrong. Please try again.';
        setLoading(el, '#rf-submit', false);
      }
    });
    return el;
  }

  function outcomeStep(results) {
    const el = document.createElement('div');
    el.className = 'rf-step';
    el.innerHTML = `
      <h2>Return Summary</h2>
      <div class="rf-outcomes">
        ${results.map((r) => `
          <div class="rf-outcome rf-outcome-${r.kind}">
            <strong>${outcomeTitle(r.kind)}</strong>
            <p>${escapeHtml(r.message)}</p>
          </div>
        `).join('')}
      </div>
      <p class="rf-sub">Questions? Reach us at <a href="mailto:${escapeHtml(SUPPORT_EMAIL)}">${escapeHtml(SUPPORT_EMAIL)}</a>.</p>
      <button class="rf-btn-secondary" id="rf-start-new">Start a New Return</button>
    `;
    el.querySelector('#rf-start-new').addEventListener('click', () => resetWizard());
    return el;
  }

  function resetWizard() {
    state.wizardToken = null;
    state.order = null;
    state.lineItems = [];
    state.queue = [];
    state.current = null;
    state.finalSelections = [];
    state.resolvedItems = [];
    render(startStep());
  }

  function outcomeTitle(kind) {
    return {
      approved: '✅ Approved',
      warranty_review_required: '🔍 Pending Warranty Review',
      denied: '❌ Not Eligible',
      error: '⚠️ Needs Follow-up',
    }[kind] || kind;
  }

  // ── Helpers ─────────────────────────────────────────────────────────────

  function render(el) {
    root.innerHTML = '';
    root.appendChild(el);
  }

  function setLoading(scope, selector, isLoading) {
    const btn = scope.querySelector(selector);
    if (!btn) return;
    btn.disabled = isLoading;
    btn.dataset.originalText = btn.dataset.originalText || btn.textContent;
    btn.textContent = isLoading ? 'Please wait…' : btn.dataset.originalText;
  }

  async function postJson(path, body) {
    const res = await fetch(`${API_BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
})();
